import { useCallback, useEffect, useRef, useState } from 'react';
import { getDeviceUuid, getMedications } from '../api';

// A failed read is unknown, not an empty list. Account changes invalidate older replies.
export default function usePermissionMedications(provided = null) {
    const [state, setState] = useState({ rows: null, error: false });
    const generation = useRef(0);
    const active = useRef(false);
    const pending = useRef(null);
    const queuedReload = useRef(false);
    const identity = useRef(null);
    const reload = useCallback(async () => {
        if (Array.isArray(provided) || !active.current) return;
        const uuid = getDeviceUuid();
        if (pending.current?.uuid === uuid) { queuedReload.current = true; return; }
        const changed = identity.current !== uuid;
        identity.current = uuid;
        if (changed) setState({ rows: null, error: false });
        const request = { uuid, generation: ++generation.current };
        pending.current = request;
        try {
            const rows = await getMedications(uuid);
            if (!Array.isArray(rows)) throw new Error('invalid medication response');
            if (active.current && request.generation === generation.current && uuid === getDeviceUuid()) {
                setState({ rows, error: false });
            }
        } catch (_) {
            if (active.current && request.generation === generation.current && uuid === getDeviceUuid()) {
                setState(prev => ({ ...prev, error: true }));
            }
        } finally {
            if (pending.current === request) {
                pending.current = null;
                if (queuedReload.current) { queuedReload.current = false; reload(); }
            }
        }
    }, [provided]);
    useEffect(() => {
        if (Array.isArray(provided)) return undefined;
        active.current = true;
        const visible = () => { if (!document.hidden) reload(); };
        const contextChanged = () => {
            generation.current += 1;
            pending.current = null;
            queuedReload.current = false;
            identity.current = null;
            setState({ rows: null, error: false });
            reload();
        };
        window.addEventListener('focus', reload);
        window.addEventListener('native-app-foreground', reload);
        document.addEventListener('visibilitychange', visible);
        window.addEventListener('kkomok-account-context-changed', contextChanged);
        window.addEventListener('medications-updated', reload);
        reload();
        return () => {
            active.current = false;
            generation.current += 1;
            pending.current = null;
            queuedReload.current = false;
            window.removeEventListener('focus', reload);
            window.removeEventListener('native-app-foreground', reload);
            document.removeEventListener('visibilitychange', visible);
            window.removeEventListener('kkomok-account-context-changed', contextChanged);
            window.removeEventListener('medications-updated', reload);
        };
    }, [provided, reload]);
    return { medications: Array.isArray(provided) ? provided : state.rows,
        error: !Array.isArray(provided) && state.error, reload };
}
